import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import PerfectScrollbar from 'react-perfect-scrollbar';

import ConstituentSelect from '../ConstituentSelect/ConstituentSelect';
import InputItem from '../Forms/InputItem/InputItem';
import CustomSelect from '../Forms/CustomSelect/CustomSelect';
import FormButton from '../Forms/FormButton/FormButton';
import Fader from '../Loaders/Fader';

import {
  FEEDBACK_TYPES,
  IMPORTANCE_TYPES,
  SOCIAL_SERVICES_SUBTYPES,
  USER_ROLES,
} from '../../../util/constants/AppConstants';
import { setNotify } from '../../../datastore/actions/actionActions';
import { checkForEmptyString, checkPostalCode } from '../../../util/HelperFunctions';
import { getConstituents, getCouncillorConstituents } from '../../../http/constituentAPI';
import { createFeedback, saveFeedback } from '../../../http/feedbackAPI';
import useOutsideClick from '../../../hooks/useOutsideClick';

import './FeedbackForm.scss';

const FeedbackForm = ({ isOpen, onCloseHandler, feedbackData, handleGetFeedbackData, userInfo }) => {
  const dispatch = useDispatch();

  const [constituents, setConstituents] = useState([]);
  const [loadingConstituents, setLoadingConstituents] = useState(false);
  const [selectedConstituent, setSelectedConstituent] = useState(null);
  const [createMode, setCreateMode] = useState(false);

  const [feedbackFromCall, setFeedbackFromCall] = useState(false);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [address, setAddress] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [type, setType] = useState('');
  const [subtype, setSubtype] = useState('');
  const [importance, setImportance] = useState(IMPORTANCE_TYPES[2].value);
  const [description, setDescription] = useState('');
  const [memberNotes, setMemberNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const config = useSelector((state) => state.config.config);

  const windowRef = useRef();

  const mppRoleValue = USER_ROLES.find((role) => role.short === 'MPP').value;
  const councilorRoleValue = USER_ROLES.find((role) => role.short === 'Councillor').value;

  const isCouncillor = userInfo && userInfo.role === councilorRoleValue;

  useEffect(() => {
    if (feedbackData) {
      if (feedbackData.constituent_id) {
        setSelectedConstituent({
          id: feedbackData.constituent_id,
          name: feedbackData.name,
          phone: feedbackData.phone,
          email: feedbackData.email,
          address: feedbackData.address,
          postal_code: feedbackData.postal_code,
        });
      } else {
        setName(feedbackData.name);
        setPhone(feedbackData.phone);
        setEmail(feedbackData.email);
        setAddress(feedbackData.address);
        setPostalCode(feedbackData.postal_code);
      }
      setFeedbackFromCall(feedbackData.source === 'Phone');
      setType(feedbackData.type);
      setSubtype(feedbackData.subtype);
      setImportance(feedbackData.importance);
      setDescription(feedbackData.description);
      setMemberNotes(feedbackData.member_notes);
    }
  }, [feedbackData]);

  const loadConstituents = async (userId) => {
    setLoadingConstituents(true);

    const res = isCouncillor ? await getCouncillorConstituents(config, userId) : await getConstituents(config, userId);

    if (res.status === 200) {
      setConstituents(res.data.constituents);
    } else {
      console.log('ERROR: Grabbing Constituents');
    }

    setLoadingConstituents(false);
  };

  useEffect(() => {
    if (config.api && userInfo && isOpen) {
      loadConstituents(userInfo.id);
    }
  }, [config, userInfo, isOpen]);

  const checkIsValid = () => {
    if (((!createMode && selectedConstituent) || (createMode && name && postalCode)) && type) {
      return true;
    }

    return false;
  };

  const getPayload = () => {
    const constituentData = selectedConstituent
      ? {
          constituent_id: selectedConstituent.id,
          name: selectedConstituent.name,
          phone: selectedConstituent.phone,
          email: selectedConstituent.email,
          address: selectedConstituent.address,
          postal_code: selectedConstituent.postal_code,
        }
      : {
          constituent_id: null,
          name,
          phone,
          email,
          address,
          postal_code: postalCode.toUpperCase().trim(),
        };

    return {
      ...constituentData,
      type,
      subtype: type === 'Social Services' ? subtype : '',
      importance,
      description,
      member_notes: memberNotes,
    };
  };

  const handleSuccess = (message) => {
    dispatch(
      setNotify({
        status: true,
        type: 'success',
        title: 'Success',
        message,
      }),
    );

    handleGetFeedbackData();
    onCloseHandler();
  };

  const showError = (message) => {
    dispatch(
      setNotify({
        status: true,
        type: 'error',
        title: 'Error',
        message,
      }),
    );
  };

  const handleSave = async () => {
    if (!checkIsValid()) {
      return showError('Fill in all required fields');
    }

    const payload = getPayload();

    if (!checkPostalCode(payload.postal_code)) {
      return showError(
        'Invalid postal code format. Please enter a 6-character code in the format C#C#C# (C - letter, # - digit)',
      );
    }

    setSaving(true);

    const res = await saveFeedback(
      config,
      feedbackData.id,
      userInfo.id,
      payload.constituent_id,
      payload.name,
      payload.phone,
      payload.email,
      payload.address,
      payload.postal_code,
      payload.type,
      payload.subtype,
      payload.importance,
      payload.description,
      payload.member_notes,
      feedbackData.source === 'Manual' || feedbackData.source === 'Phone'
        ? feedbackFromCall
          ? 'Phone'
          : 'Manual'
        : feedbackData.source,
    );

    if (res.status === 200) {
      handleSuccess(res.data.message);
    } else {
      dispatch(
        setNotify({
          status: true,
          type: 'error',
          title: 'Error',
          message: res.data.message,
        }),
      );
    }
    setSaving(false);
  };

  const handleCreate = async () => {
    if (!checkIsValid()) {
      return showError('Fill in all required fields');
    }

    const payload = getPayload();

    if (!checkPostalCode(payload.postal_code)) {
      return showError(
        'Invalid postal code format. Please enter a 6-character code in the format C#C#C# (C - letter, # - digit)',
      );
    }

    setSaving(true);

    const res = await createFeedback(
      config,
      userInfo.id,
      payload.constituent_id,
      payload.name,
      payload.phone,
      payload.email,
      payload.address,
      payload.postal_code,
      payload.type,
      payload.subtype,
      payload.importance,
      payload.description,
      payload.member_notes,
      feedbackFromCall ? 'Phone' : 'Manual',
    );

    if (res.status === 201) {
      handleSuccess(res.data.message);
    } else {
      dispatch(
        setNotify({
          status: true,
          type: 'error',
          title: 'Error',
          message: res.data.message,
        }),
      );
    }
    setSaving(false);
  };

  useOutsideClick(windowRef, null, isOpen, onCloseHandler);

  return ReactDOM.createPortal(
    <div className="login-wrap feedback">
      <div className={`login-view ${saving ? 'saving' : ''}`}>
        <div ref={windowRef} className="login-box feedback__box">
          {!saving ? (
            <div className="feedback__title">{feedbackData ? 'Edit Email' : 'Create Email'}</div>
          ) : (
            <>
              <div className="info__title">{feedbackData ? 'Saving Email' : 'Creating Email'}</div>
              <div className="info__text">The process should take no longer than 30 seconds. Please Wait...</div>
            </>
          )}
          {!saving ? (
            <>
              <div className="feedback__wrapper">
                <PerfectScrollbar
                  options={{
                    wheelPropagation: false,
                    autoHide: false,
                  }}
                >
                  <div className="feedback__form">
                    <div className="feedback__section">
                      <div className="feedback__section-title">Constituent Information</div>
                      {!selectedConstituent && !createMode && (
                        <>
                          <ConstituentSelect
                            label="Constituent *"
                            constituents={constituents}
                            loading={loadingConstituents}
                            setSelectedConstituent={setSelectedConstituent}
                          />

                          <div className="feedback__form-btn" onClick={() => setCreateMode(true)}>
                            + Create new constituent
                          </div>
                        </>
                      )}
                      {selectedConstituent && (
                        <div className="feedback__selected">
                          <div>Name: {checkForEmptyString(selectedConstituent.name)}</div>
                          <div>Email: {checkForEmptyString(selectedConstituent.email)}</div>
                          <div>Phone: {checkForEmptyString(selectedConstituent.phone)}</div>
                          <div>Address: {checkForEmptyString(selectedConstituent.address)}</div>
                          <div>Postal Code: {checkForEmptyString(selectedConstituent.postal_code)}</div>

                          <div className="feedback__form-btn" onClick={() => setSelectedConstituent(null)}>
                            Change
                          </div>
                        </div>
                      )}
                      {createMode && (
                        <>
                          <InputItem
                            label="Name *"
                            type="text"
                            onChange={(e) => setName(e.target.value)}
                            value={name}
                          />
                          <InputItem
                            label="Phone"
                            type="text"
                            onChange={(e) => setPhone(e.target.value)}
                            value={phone}
                          />
                          <div className="feedback__hint">Multiple values should be separated with a semicolon (;)</div>
                          <InputItem
                            label="Email"
                            type="email"
                            onChange={(e) => setEmail(e.target.value)}
                            value={email}
                          />
                          <div className="feedback__hint">Multiple values should be separated with a semicolon (;)</div>
                          <InputItem
                            label="Address"
                            type="text"
                            onChange={(e) => setAddress(e.target.value)}
                            value={address}
                          />
                          <InputItem
                            label="Postal Code *"
                            type="text"
                            onChange={(e) => setPostalCode(e.target.value)}
                            value={postalCode}
                          />
                          <div className="feedback__hint">Format: C#C#C# (C - letter, # - digit), e.g. A1A1A1</div>
                          <div className="feedback__form-btn" onClick={() => setCreateMode(false)}>
                            Select from existing
                          </div>
                        </>
                      )}
                    </div>
                    <div className="feedback__section">
                      <div className="feedback__section-title">Email Information</div>
                      {(!feedbackData ||
                        (feedbackData && (feedbackData.source === 'Manual' || feedbackData.source === 'Phone'))) && (
                        <div className="feedback__call-question">
                          <span className="feedback__question-label">Was this from a phone call?</span>
                          <div className="feedback__question-buttons">
                            <button
                              type="button"
                              className={`feedback__question-btn ${feedbackFromCall ? 'active' : ''}`}
                              onClick={() => setFeedbackFromCall(true)}
                            >
                              Yes
                            </button>
                            <button
                              type="button"
                              className={`feedback__question-btn ${!feedbackFromCall ? 'active' : ''}`}
                              onClick={() => setFeedbackFromCall(false)}
                            >
                              No
                            </button>
                          </div>
                        </div>
                      )}
                      <CustomSelect
                        label="Type *"
                        options={FEEDBACK_TYPES}
                        selectedOption={type}
                        setSelectedOption={setType}
                      />
                      {type === 'Social Services' && (
                        <CustomSelect
                          label="Subtype"
                          options={SOCIAL_SERVICES_SUBTYPES}
                          selectedOption={subtype}
                          setSelectedOption={setSubtype}
                        />
                      )}
                      <CustomSelect
                        label="Importance"
                        options={IMPORTANCE_TYPES}
                        selectedOption={importance}
                        setSelectedOption={setImportance}
                      />
                      <InputItem
                        label="Description"
                        onChange={(e) => setDescription(e.target.value)}
                        value={description}
                        isText
                      />
                      {userInfo && (userInfo.role === mppRoleValue || userInfo.role === councilorRoleValue) && (
                        <InputItem
                          label="Member notes"
                          onChange={(e) => setMemberNotes(e.target.value)}
                          value={memberNotes}
                          isText={true}
                          className="notes"
                        />
                      )}
                    </div>
                  </div>
                </PerfectScrollbar>
              </div>
              <div className="submit">
                <FormButton
                  subLink={'Cancel'}
                  subLinkClick={onCloseHandler}
                  text={feedbackData ? 'Save' : 'Submit'}
                  onClick={feedbackData ? handleSave : handleCreate}
                />
              </div>
            </>
          ) : (
            <Fader size={50} />
          )}
        </div>
      </div>
    </div>,
    document.getElementById('root'),
  );
};

export default FeedbackForm;
